import {
  Container,
  Stack,
  Box,
  Text,
  HStack,
  Divider,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Flex,
  Badge,
  Image,
  Wrap,
  Button,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { FaGithub } from "react-icons/fa";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import t from "../translations";

function ProjectCard({ project, color, index }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 25 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
      whileHover={{ y: -3 }}
      style={{ borderRadius: "var(--chakra-radii-xl)" }}
    >
      <Card
        size="sm"
        borderRadius="xl"
        _hover={{ boxShadow: "xl" }}
        transition="box-shadow 0.25s ease"
      >
        <CardHeader>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <HStack spacing={3}>
              <Image
                src={project.image}
                h={10}
                w={10}
                objectFit="contain"
                alt={`${project.title} logo`}
                borderRadius="md"
              />
              <Box>
                <Text fontWeight={600}>{project.title}</Text>
                <Text fontSize="sm" color="gray.500">
                  {project.subtitle}
                </Text>
              </Box>
            </HStack>
            {project.githubLink && (
              <Button
                as="a"
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                size={{ base: "xs", md: "sm" }}
                leftIcon={<FaGithub />}
                variant="outline"
                colorScheme={color}
                flexShrink={0}
              >
                {t.projects.githubButton}
              </Button>
            )}
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <Stack spacing={2}>
            {project.listItems.map((item, i) => (
              <Flex key={i} fontSize="sm" align="flex-start" gap={2}>
                <ChevronRightIcon color={`${color}.400`} mt="4px" flexShrink={0} />
                <Text>{item}</Text>
              </Flex>
            ))}
          </Stack>
        </CardBody>
        <CardFooter pt={0}>
          <Wrap spacing={2}>
            {project.badges.map((badge) => (
              <Badge
                key={badge.name}
                colorScheme={badge.colorScheme}
                fontSize="xs"
                borderRadius="full"
                px={2}
                py={0.5}
              >
                {badge.name}
              </Badge>
            ))}
          </Wrap>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

export default function Projects({ color }) {
  return (
    <Container maxW="4xl" id="projects" py={{ base: 16, md: 24 }}>
      <Stack spacing={{ base: 8, md: 10 }}>
        <Stack align="center" direction="row" px={4}>
          <HStack mx={4}>
            <Text fontWeight={800} fontSize={{ base: "xl", md: "2xl" }} whiteSpace="nowrap">
              {t.projects.heading}
            </Text>
          </HStack>
          <Divider />
        </Stack>
        <Stack px={4} spacing={6}>
          {t.projects.data.map((project, i) => (
            <ProjectCard
              key={project.title}
              project={project}
              color={color}
              index={i}
            />
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}
